import type { Router } from "vue-router";

/**
 * Every view is a lazily imported chunk with a content hash in its name, and a deploy deletes
 * the previous build's. A tab opened before one still holds the old module graph, so the next
 * route change asks for a file that is gone: the import rejects, vue-router aborts the
 * navigation, and with no error handler the app stops responding to clicks. Reload instead,
 * landing on the route they asked for.
 */

/** Same failure, four wordings — plus Vite's own helper for a stylesheet. */
const STALE_CHUNK_MESSAGES = [
  "Failed to fetch dynamically imported module", // Chromium
  "error loading dynamically imported module", // Firefox
  "Importing a module script failed", // Safari
  "Unable to preload CSS", // Vite preload helper
];

/** A reload that did not help must not turn into a reload loop. */
const RETRY_MARKER = "n7ait:stale-chunk-reload";
const RETRY_WINDOW_MS = 10_000;

export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return STALE_CHUNK_MESSAGES.some((known) => message.includes(known));
}

function readRetriedPath(): { path: string; at: number } | null {
  try {
    const raw = sessionStorage.getItem(RETRY_MARKER);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // Private mode / blocked storage: no memory of a retry, so we allow one.
    return null;
  }
}

function hasJustRetried(path: string): boolean {
  const marker = readRetriedPath();
  return !!marker && marker.path === path && Date.now() - marker.at < RETRY_WINDOW_MS;
}

function markRetried(path: string): void {
  try {
    sessionStorage.setItem(RETRY_MARKER, JSON.stringify({ path, at: Date.now() }));
  } catch {
    // Losing the marker only costs us the loop guard; the reload still helps.
  }
}

export function recoverFromStaleChunks(router: Router): void {
  router.onError((error, to) => {
    if (!isStaleChunkError(error)) return;

    // Reloading did not bring the chunk back — the network is down or the file
    // really is missing. Let the error surface rather than reloading forever.
    if (hasJustRetried(to.fullPath)) return;

    markRetried(to.fullPath);
    window.location.assign(to.fullPath);
  });
}
